import CommonService from "../Common";
import { AxiosRequestConfig } from "axios";

export interface ListAdInsightsParams {
  userId: string;
  projectId?: string;
  strategyId?: string;
  name: string;
  sortBy?: "createdAt" | "updatedAt" | "metadata.generatedAt";
  sortOrder?: "asc" | "desc";
  limit?: number;
  page?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface GenerateAdInsightsParams {
  userId: string | any;
  strategyId: string | any;
  modelAPIKey: string | any;
  socketId?: string;
  priority?: number;
  customInstructions?: string;
  platforms?: string[];
  campaignData: File | Blob;
}

export interface GetGenerationStatusParams {
  userId: string;
  jobId?: string;
}

class AdInsightsService {
  private readonly pathPrefix: string;
  private readonly commonService: CommonService;

  constructor() {
    this.pathPrefix = "/api/v1/projects";
    this.commonService = new CommonService();
  }

  // GET /api/v1/projects/:projectId/adinsights/:insightsId
  async fetchInsight(projectId: string | any, insightsId: string | any) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}/adinsights/${insightsId}`,
      withCredentials: true,
    };

    const insight = await this.commonService.fetch(config);

    // Parse insights data with platform-specific handling
    const parsedInsight = {
      ...insight,
      insights: {
        patterns: this.parseInsightData(insight.insights?.patterns),
        recommendations: this.parseInsightData(
          insight.insights?.recommendations
        ),
      },
    };

    return parsedInsight;
  }

  // GET /api/v1/projects/:projectId/adinsights
  async listInsights(projectId: string, params?: ListAdInsightsParams) {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${this.pathPrefix}/${projectId}/adinsights`,
      params: {
        ...params,
      },
      withCredentials: true,
    };

    const response = await this.commonService.fetch(config);

    // Parse insights data with platform-specific handling
    const parsedInsights = response.data.insights.map((insight: any) => ({
      ...insight,
      insights: {
        patterns: this.parseInsightData(insight.insights?.patterns),
        recommendations: this.parseInsightData(
          insight.insights?.recommendations
        ),
      },
    }));

    return {
      ...response.data,
      insights: parsedInsights,
    };
  }

  // POST /api/v1/projects/:projectId/adinsights/generate
  async startInsightsGeneration(
    projectId: string,
    params: GenerateAdInsightsParams | any
  ) {
    this.validateCampaignData(params.campaignData);

    // Create FormData for file upload
    const formData = new FormData();
    Object.entries(params).forEach(([key, value]: any) => {
      formData.append(key, value);
    });

    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${projectId}/adinsights/generate`,
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      withCredentials: true,
    };

    return this.commonService.fetch(config);
  }

  // DELETE /api/v1/projects/:projectId/adinsights
  async deleteInsights(projectId: string, insightIds: string[]) {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      url: `${this.pathPrefix}/${projectId}/adinsights`,
      data: {
        insightIds,
      },
      withCredentials: true,
    };

    return this.commonService.fetch(config);
  }

  // POST /api/v1/projects/:projectId/adinsights/:insightsId/qa
  async answerQuestionOnDocument(
    userId: string | any,
    projectId: string | any,
    insightsId: string | any,
    question: string,
    modelAPIKey: string | any
  ) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${this.pathPrefix}/${projectId}/adinsights/${insightsId}/qa`,
      data: { question, userId, modelAPIKey },
      withCredentials: true,
    };
    return this.commonService.fetch(config);
  }

  // Helper method to safely parse JSON strings
  private parseInsightData(data: any): any {
    if (!data) return null;

    try {
      if (typeof data === "string") {
        return JSON.parse(data);
      }
      return data;
    } catch (error) {
      console.warn("Error parsing insight data:", error);
      return data; // Return original data if parsing fails
    }
  }

  // Helper method to validate campaign data file
  private validateCampaignData(file: File | Blob) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error("Campaign data file size exceeds 10MB limit");
    }
    return true;
  }
}

export default AdInsightsService;
